import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, RefreshCw, Trash2, Plug } from "lucide-react";

/**
 * XERO CONNECTED ORGS — one row per connection × tenant.
 * Hidden entirely when the workspace has no Xero connections AND the backend
 * isn't configured (keeps `/app/sources` clean for teams that don't use Xero).
 */
export default function XeroConnectionsPanel({ onMutate }) {
  const [data, setData] = useState(null); // { configured, connections: [] }
  const [syncing, setSyncing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/connectors/xero/status");
      setData(r.data);
    } catch {
      setData({ configured: false, connections: [] });
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) return null;
  const { configured, connections } = data;
  if (!configured && connections.length === 0) return null;

  const sync = async (id) => {
    setSyncing(id);
    try {
      const r = await api.post(`/connectors/xero/${id}/sync`);
      toast.success(`Xero synced · ${r.data?.rows?.toLocaleString?.() || 0} rows across ${r.data?.sources?.length || 0} sources.`);
      await load();
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
    setSyncing(null);
  };

  const disconnect = async (id) => {
    if (!window.confirm("Disconnect this Xero connection? Imported data sources remain but stop refreshing.")) return;
    setRemoving(id);
    try {
      await api.delete(`/connectors/xero/${id}`);
      toast.success("Xero disconnected.");
      await load();
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setRemoving(null);
  };

  return (
    <section
      data-testid="xero-connections-panel"
      className="border border-zinc-900 bg-zinc-950/40"
    >
      <header className="px-4 py-2.5 border-b border-zinc-900 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-3.5 h-3.5 text-[#FFB000]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            // XERO · CONNECTED ORGS
          </span>
          <span className="font-mono text-[9px] text-zinc-600">
            {connections.length} connection{connections.length === 1 ? "" : "s"}
          </span>
        </div>
        <span
          className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
            configured
              ? "text-[#00E676] border-[#00E676]/30"
              : "text-[#FFB000] border-[#FFB000]/30"
          }`}
        >
          {configured ? "CONFIGURED" : "NOT CONFIGURED"}
        </span>
      </header>

      {connections.length === 0 ? (
        <div className="p-4 flex items-center gap-3 text-xs text-zinc-500">
          <Plug className="w-4 h-4 text-zinc-700" />
          <span>
            No Xero organisations connected yet. Tap <span className="text-[#FFB000]">+ ADD SOURCE → XERO</span> to authorize.
          </span>
        </div>
      ) : (
        <div className="divide-y divide-zinc-900">
          {connections.map((c) => (
            <div
              key={c.id}
              data-testid={`xero-connection-${c.id}`}
              className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2 items-center">
                  {(c.tenants || []).map((t) => (
                    <span
                      key={t.tenant_id}
                      className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-zinc-800 text-zinc-300"
                      title={t.tenant_id}
                    >
                      {t.tenant_name}
                    </span>
                  ))}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  <span>
                    LAST SYNC:{" "}
                    <span className="text-zinc-300">
                      {c.last_synced_at
                        ? new Date(c.last_synced_at).toLocaleString()
                        : "Never"}
                    </span>
                  </span>
                  <span>
                    CONNECTED:{" "}
                    <span className="text-zinc-300">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </span>
                  </span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                <button
                  onClick={() => sync(c.id)}
                  disabled={syncing === c.id}
                  data-testid={`xero-sync-${c.id}`}
                  className="flex-1 sm:flex-none min-h-[44px] border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:text-[#FFB000] hover:border-[#FFB000]/50 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-3 h-3 ${syncing === c.id ? "animate-spin" : ""}`}
                  />
                  {syncing === c.id ? "SYNCING" : "SYNC NOW"}
                </button>
                <button
                  onClick={() => disconnect(c.id)}
                  disabled={removing === c.id}
                  data-testid={`xero-disconnect-${c.id}`}
                  className="min-h-[44px] min-w-[44px] border border-zinc-800 px-3 py-2 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 transition-colors disabled:opacity-50 inline-flex items-center justify-center"
                  title="Disconnect"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
